import Button from 'components/helpers/Button';
import Search from 'components/authorable/site/Header/SvgSearch/search';
import { classnames } from 'tailwindcss-classnames';
import NrcMenuClose from 'components/authorable/site/Header/SvgMenuClose/nrc-menu-close';
import NrcMenu from 'components/authorable/site/Header/SvgMenu/nrc-menu';
import NextLink from 'next/link';
import { pageorLink } from 'components/authorable/site/Footer/Footer';
import { CollectionStackItem } from './NmdpSiteHeaderTypes';
import { useCartContext } from 'components/globalContext/CartContext';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import {
  ImageField,
  LinkField,
  Image as SitecoreImage,
  useSitecoreContext,
  Field,
} from '@sitecore-jss/sitecore-jss-react';
import {
  utilityLinkClass,
  animateNavOpen,
  animateNavClose,
  mobileMainNav,
  toggleButton,
  hasSubmenu,
} from './NmdpSiteHeaderClasses';
import { menuLocation, pageNavigationDataLayerHandler } from 'lib/util/gtm-datalayer';

type TabTopContainerProps = {
  toggleMobileMenu: () => void;
  isOpen: boolean;
  placeholderText: string;
  logo: ImageField;
  logoText: string;
  giveMoneyButton: LinkField;
  joinRegistryButton: LinkField;
};

const tabletBtnClass = classnames(
  'xl:mr-2',
  'lg:mr-2',
  'mb-0',
  'px-0',
  'text-xs',
  'sm:leading-5',
  'leading-1',
  'text-center',
  'w-[126px]',
  'h-[35px]',
  'xl:pt-1',
  'lg:pt-1'
);

export const TabNavigation = ({
  isOpen,
  collectionStack,
  getBackButton,
  mobileItems,
  utilityLinks,
}: {
  isOpen: boolean;
  collectionStack: CollectionStackItem[];
  getBackButton: () => React.ReactNode;
  mobileItems: React.ReactNode;
  utilityLinks: pageorLink[];
}) => {
  const fields = collectionStack[1]?.fields;
  const { sitecoreContext } = useSitecoreContext();
  const cartLink = sitecoreContext?.cartLink as LinkField;
  const { cartContext } = useCartContext();
  const cartLinkwithCount = {
    ...cartLink?.value,
    text: `${cartLink?.value?.text || ''} (${cartContext})`,
  };
  return (
    <>
      <nav
        className={classnames(
          mobileMainNav,
          isOpen ? animateNavOpen : animateNavClose,
          collectionStack.length > 1 ? 'state-open' : null
        )}
      >
        {getBackButton()}
        {collectionStack.length === 2 && fields && (
          <>
            {fields?.navigationLink && (
              <div className={classnames('max-h-[25px]', 'p-4')}>
                <LinkA11yWrapper
                  editable={false}
                  field={fields.navigationLink}
                  className={classnames('text-blue', 'font-medium')}
                />
              </div>
            )}
            {fields?.description?.value && (
              <p
                className={classnames(
                  'text-gray-darkGray',
                  'py-2',
                  'mx-4',
                  'my-2',
                  'border-b',
                  'border-gray-whisper'
                )}
              >
                {fields?.description?.value}
              </p>
            )}
          </>
        )}

        <ul role="menubar">{mobileItems}</ul>
        <ul>
          {utilityLinks?.map((item) => (
            <li role="menuitem" key={item.name} className={hasSubmenu} aria-label="Menu item">
              <LinkA11yWrapper
                className={classnames(utilityLinkClass, 'px-4', 'py-7', 'font-medium')}
                editable={false}
                suppressNewTabIcon={false}
                field={
                  item.fields.navigationItemLink
                    ? (item.fields.navigationItemLink as LinkField)
                    : {
                        value: {
                          href: item.url,
                          text: (item.fields.pageTitle as Field<string>).value
                            ? (item.fields.pageTitle as Field<string>).value
                            : item.displayName,
                        },
                      }
                }
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e, menuLocation[0]);
                }}
              />
            </li>
          ))}
        </ul>
      </nav>
      <ul
        role="menu"
        className={classnames(
          'flex',
          'flex-col',
          'w-full',
          'px-4',
          'text-white',
          'justify-end',
          'border-t',
          'border-gray-midGray',
          'fixed',
          'bottom-0',
          'bg-white',
          'z-40',
          isOpen ? animateNavOpen : animateNavClose,
          collectionStack.length > 1 ? 'state-open' : null
        )}
      >
        {cartContext > 0 && cartLink?.value?.href?.length && (
          <li
            role="menuitem"
            key={cartLink.value.text}
            className={classnames('py-3')}
            aria-label="Menu item"
          >
            <LinkA11yWrapper
              field={{ value: cartLinkwithCount }}
              className={classnames(utilityLinkClass)}
            />
          </li>
        )}
      </ul>
    </>
  );
};

export const TabTopContainer = ({
  toggleMobileMenu,
  isOpen,
  logo,
  placeholderText,
  logoText,
  giveMoneyButton,
  joinRegistryButton,
}: TabTopContainerProps): JSX.Element => {
  return (
    <div className={classnames('xl:block', 'border-blue-navy')}>
      <div
        className={classnames(
          'flex',
          'justify-between',
          'items-center',
          'border-b-2',
          'border-gray-whisper'
        )}
      >
        <div className={classnames('flex', 'justify-between', 'items-center')}>
          <div className={classnames('self-start')}>
            <button
              type="button"
              aria-label="Toggle navigation menu"
              className={toggleButton}
              data-id="header-mobile-menu-toggle"
              onClick={toggleMobileMenu}
            >
              <div
                className={classnames(
                  'align-middle',
                  'flex',
                  'flex-col-reverse',
                  'hover:text-blue-navy',
                  'justify-between',
                  'text-blue',
                  'text-body',
                  'w-full'
                )}
              >
                <span
                  className={classnames('leading-2', 'px-2.5', 'pt-1', 'text-blue-navy', 'text-xs')}
                >
                  {isOpen ? 'Close' : 'Menu'}
                </span>
                {isOpen ? (
                  <NrcMenuClose
                    className={classnames('mx-auto')}
                    style={{ width: '18px', height: '18px' }}
                  />
                ) : (
                  <NrcMenu
                    className={classnames('mx-auto')}
                    style={{ width: '28px', height: '24px' }}
                  />
                )}
              </div>
            </button>
          </div>
          <div className={classnames('flex')}>
            <NextLink href="/" locale={false}>
              <a href="/" aria-label="Menu item">
                <SitecoreImage field={logo} className={classnames('h-[49px]', 'w-[151px]')} />
              </a>
            </NextLink>
            {logoText !== '' && (
              <div className={classnames('flex', 'items-center', 'ml-2')}>
                <NextLink href="/" locale={false}>
                  <a
                    href="/"
                    className={classnames('text-blue-navy', 'font-bold', 'font-dmSans', 'text-xl')}
                  >
                    {logoText}
                  </a>
                </NextLink>
              </div>
            )}
          </div>
        </div>
        <div className={classnames('flex', 'items-center')}>
          <div
            className={classnames(
              'relative',
              'bg-gradient-to-r',
              'from-blue',
              'to-green',
              'p-0',
              'lg:bg-none',
              'bg-none',
              'tab-search'
            )}
          >
            <Search searchplaceholderText={placeholderText} />
          </div>
          {giveMoneyButton?.value && (
            <div className={classnames('pr-1')}>
              <Button linkField={giveMoneyButton} className={tabletBtnClass} />
            </div>
          )}
          {joinRegistryButton?.value && (
            <div className={classnames('pr-4')}>
              <Button linkField={joinRegistryButton} className={tabletBtnClass} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
